// TopNavTabs.js
import React from "react";
import { NavLink } from "react-router-dom";
import "../styles/TopNavTabs.css";

/**
 * Props:
 * - items: [{ key, label, path, badge }]
 * - className: extra wrapper classes
 * - tabStyle: inline style for each tab
 * - activeKey: if provided, component acts controlled and uses onTabClick (no NavLink navigation)
 * - onTabClick: function (key, item) -> called when tab clicked (for controlled usage)
 *
 * Usage in AdminUsersPage (controlled):
 * <TopNavTabs items={tabs} activeKey={activeTab} onTabClick={(k)=>setActiveTab(k)} />
 *
 * Usage in router mode:
 * <TopNavTabs items={tabsWithPath} />
 */
export default function TopNavTabs({
  items = [],
  className = "",
  tabStyle = {},
  activeKey = null,
  onTabClick = null,
}) {
  const isControlled = typeof activeKey === "string" && typeof onTabClick === "function";

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 640;
  const useDropdown = isMobile && items.length > 4;

  const activeItem = items.find(i => i.key === activeKey) || items[0];

  if (useDropdown) {
    return (
      <nav className={`topnav-tabs ${className}`} aria-label="Top navigation tabs">
        <div className="topnav-inner">

          <select
            className="topnav-dropdown"
            value={activeItem?.key}
            onChange={(e)=>{
              const key = e.target.value;
              const item = items.find(i=>i.key === key);

              if (isControlled) {
                onTabClick(key, item);
              } else if (item?.path) {
                window.location.href = item.path;
              }
            }}
          >
            {items.map(it => (
              <option key={it.key} value={it.key}>
                {it.label}
              </option>
            ))}
          </select>

        </div>
      </nav>
    );
  }

  return (
    <nav className={`topnav-tabs ${className}`} aria-label="Top navigation tabs">
      <div className="topnav-inner">
        <ul className="topnav-list">
          {items.map((it) => {
            const active = isControlled ? it.key === activeKey : false;
            return (
              <li key={it.key} className="topnav-item">
                {isControlled ? (
                  <button
                    type="button"
                    className={`topnav-btn ${active ? "active" : ""}`}
                    onClick={() => onTabClick(it.key, it)}
                    style={tabStyle}
                    aria-current={active ? "true" : "false"}
                  >
                    <span className="topnav-label">{it.label}</span>
                    {it.badge ? <span className="topnav-badge">{it.badge}</span> : null}
                  </button>
                ) : (
                  <NavLink
                    exact
                    to={it.path || "#"}
                    className="topnav-link"
                    activeClassName="active"
                    style={tabStyle}
                  >
                    <span className="topnav-label">{it.label}</span>
                    {it.badge ? <span className="topnav-badge">{it.badge}</span> : null}
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
